/**
 * Save migration (Phase 2). One entry point — `migrateSave` — used by BOTH
 * the localStorage load path and the import path.
 *
 * v1/v2 saves carried a flat dense bank, a dense inventory, and 4 equipment
 * keys. v3 fixes the layouts: 64 explicit bank slots (4 tabs × 16), 28
 * explicit inventory slots, 13 equipment keys, plus the offline-simulation
 * fields (lastSimulatedAt, actionProgressMs, hp).
 *
 * Guarantees: idempotent (migrate(migrate(x)) equals migrate(x)), never
 * silently destroys known items — incompatible legacy equipment returns to
 * inventory then bank, over-max stacks split across free slots, container
 * overflow spills into the other container, and if a known quantity still
 * cannot fit anywhere the migration fails explicitly (returns null) instead
 * of producing a lossy save. Unknown item ids are dropped (documented);
 * future versions are rejected.
 */
import { EQUIP_SLOT_IDS, ITEMS, MONSTERS_BY_ID, SKILLS_BY_ID, SKILL_IDS } from '../../data';
import type {
  ActiveSkill,
  ItemId,
  ItemStack,
  MonsterId,
  PlayerEquipment,
  PlayerState,
  SkillId,
} from '../state';
import {
  BANK_SIZE,
  INVENTORY_SIZE,
  depositIntoSlots,
  emptyBank,
  emptyEquipment,
  emptyInventory,
  maxStackOf,
  placeInInventory,
} from '../economy/containers';
import { getMonster } from '../monsters/spec';
import { STARTER_LADLE_FLAG } from '../quests/tutorial';

export const SAVE_VERSION = 4 as const;

/** First version with the fixed container layouts + offline fields. */
const FIXED_LAYOUT_VERSION = 3;

const EPOCH_ISO = new Date(0).toISOString();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isKnownItem(id: unknown): id is ItemId {
  return typeof id === 'string' && id in ITEMS.items;
}

function asStack(entry: unknown): ItemStack | null {
  if (!isRecord(entry)) return null;
  const { itemId, qty } = entry as { itemId?: unknown; qty?: unknown };
  if (!isKnownItem(itemId)) return null;
  if (typeof qty !== 'number' || !Number.isFinite(qty) || qty < 1) return null;
  return { itemId, qty: Math.floor(qty) };
}

/** A filled container plus the known stacks that did not fit in it. */
type ContainerFill = {
  slots: ReadonlyArray<ItemStack | null>;
  overflow: ItemStack[];
};

/** Legacy dense list → left-to-right fill of a fixed-size slot array. */
function fillLeftToRight(value: unknown, size: number): ContainerFill {
  let slots: ReadonlyArray<ItemStack | null> = Array<ItemStack | null>(size).fill(null);
  const overflow: ItemStack[] = [];
  if (!Array.isArray(value)) return { slots, overflow };
  for (const entry of value) {
    const stack = asStack(entry);
    if (!stack) continue;
    const deposited = depositIntoSlots(slots, stack.itemId, stack.qty);
    slots = deposited.slots;
    if (deposited.leftover > 0) overflow.push({ itemId: stack.itemId, qty: deposited.leftover });
  }
  return { slots, overflow };
}

/** Current-version array → validated fixed-size array preserving positions. */
function normalizeFixed(value: unknown, size: number): ContainerFill {
  const out = Array<ItemStack | null>(size).fill(null);
  const overflow: ItemStack[] = [];
  if (!Array.isArray(value)) return { slots: out, overflow };
  for (let i = 0; i < Math.min(size, value.length); i += 1) {
    const stack = asStack(value[i]);
    if (!stack) continue;
    const max = maxStackOf(stack.itemId);
    out[i] = { itemId: stack.itemId, qty: Math.min(stack.qty, max) };
    // Over-max quantity is never clamped away: the excess splits into
    // whatever free capacity remains (same container first, then the other).
    if (stack.qty > max) overflow.push({ itemId: stack.itemId, qty: stack.qty - max });
  }
  return { slots: out, overflow };
}

function normalizeActive(value: unknown): ActiveSkill {
  if (isRecord(value)) {
    if (value.kind === 'train' && SKILLS_BY_ID.has(value.skillId as SkillId)) {
      return { kind: 'train', skillId: value.skillId as SkillId };
    }
    if (value.kind === 'combat' && MONSTERS_BY_ID.has(value.monsterId as MonsterId)) {
      return { kind: 'combat', monsterId: value.monsterId as MonsterId, zone: 'codex_quarry' };
    }
  }
  return { kind: 'idle' };
}

function normalizeSkills(value: unknown): PlayerState['skills'] {
  const src = isRecord(value) ? value : {};
  return Object.fromEntries(
    SKILL_IDS.map((id) => {
      const entry = src[id];
      if (isRecord(entry) && typeof entry.level === 'number' && typeof entry.xp === 'number') {
        return [id, { level: entry.level, xp: entry.xp }];
      }
      return [id, { level: 1, xp: 0 }];
    }),
  ) as PlayerState['skills'];
}

function normalizeSettings(value: unknown): PlayerState['settings'] {
  const src = isRecord(value) ? value : {};
  return {
    theme: 'dark',
    soundVolume: typeof src.soundVolume === 'number' ? src.soundVolume : 0.6,
    musicVolume: typeof src.musicVolume === 'number' ? src.musicVolume : 0.4,
    combatOffline: typeof src.combatOffline === 'boolean' ? src.combatOffline : true,
    tokenMiningOffline: typeof src.tokenMiningOffline === 'boolean' ? src.tokenMiningOffline : true,
  };
}

function normalizeQuests(value: unknown): PlayerState['quests'] {
  if (!Array.isArray(value)) return [];
  const valid = value.filter(
    (q): q is { questId: string; status: 'active' | 'complete'; progress: number } =>
      isRecord(q) &&
      typeof q.questId === 'string' &&
      (q.status === 'active' || q.status === 'complete') &&
      typeof q.progress === 'number',
  );
  const deduped = new Map<string, (typeof valid)[number]>();
  for (const entry of valid) {
    const current = deduped.get(entry.questId);
    if (
      !current ||
      (entry.status === 'complete' && current.status !== 'complete') ||
      (entry.status === current.status && entry.progress > current.progress)
    ) {
      deduped.set(entry.questId, entry);
    }
  }
  return [...deduped.values()];
}

function isoOrFallback(value: unknown, fallback: string): string {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : fallback;
}

/** One-time flags (v4): strings only, deduped, insertion order kept. */
function normalizeFlags(value: unknown): ReadonlyArray<string> {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((f): f is string => typeof f === 'string'))];
}

/** Combat round counter (v4): only meaningful mid-combat. */
function normalizeCombatRound(value: unknown, active: ActiveSkill): number {
  if (active.kind !== 'combat') return 0;
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

export function migrateSave(raw: unknown): PlayerState | null {
  if (!isRecord(raw)) return null;
  const version = typeof raw.version === 'number' ? raw.version : 1;
  if (version > SAVE_VERSION) return null;
  // v3 already has the fixed layouts + hp/offline fields; v4 only ADDS
  // flags/combatRound, so both are normalized positionally, not re-packed.
  const hasFixedLayouts = version >= FIXED_LAYOUT_VERSION;

  const bankFill = hasFixedLayouts
    ? normalizeFixed(raw.bank, BANK_SIZE)
    : fillLeftToRight(raw.bank, BANK_SIZE);
  const invFill = hasFixedLayouts
    ? normalizeFixed(raw.inventory, INVENTORY_SIZE)
    : fillLeftToRight(raw.inventory, INVENTORY_SIZE);
  let bank = bankFill.slots;
  let inventory = invFill.slots;

  // Spill overflow across the remaining capacity of both containers. Known
  // items are never silently destroyed: anything that still fits nowhere
  // makes the whole migration fail explicitly.
  for (const stack of bankFill.overflow) {
    const intoBank = depositIntoSlots(bank, stack.itemId, stack.qty);
    bank = intoBank.slots;
    if (intoBank.leftover === 0) continue;
    const intoInv = depositIntoSlots(inventory, stack.itemId, intoBank.leftover);
    inventory = intoInv.slots;
    if (intoInv.leftover > 0) return null;
  }
  for (const stack of invFill.overflow) {
    const intoInv = depositIntoSlots(inventory, stack.itemId, stack.qty);
    inventory = intoInv.slots;
    if (intoInv.leftover === 0) continue;
    const intoBank = depositIntoSlots(bank, stack.itemId, intoInv.leftover);
    bank = intoBank.slots;
    if (intoBank.leftover > 0) return null;
  }

  // Initialize all 13 equipment keys; keep compatible equips, rescue the rest.
  const equipment: PlayerEquipment = emptyEquipment();
  const rescued: ItemId[] = [];
  const legacyEquipment = isRecord(raw.equipment) ? raw.equipment : {};
  for (const slot of EQUIP_SLOT_IDS) {
    const itemId = legacyEquipment[slot];
    if (!isKnownItem(itemId)) continue;
    if (ITEMS.items[itemId]?.equip?.slot === slot) equipment[slot] = itemId;
    else rescued.push(itemId);
  }
  for (const itemId of rescued) {
    const intoInventory = placeInInventory(inventory, itemId, 1);
    if (intoInventory) {
      inventory = intoInventory;
      continue;
    }
    const intoBank = depositIntoSlots(bank, itemId, 1);
    // Rescued equipment must land somewhere; a save it cannot fit into is
    // rejected rather than silently shrunk.
    if (intoBank.leftover > 0) return null;
    bank = intoBank.slots;
  }

  const active = normalizeActive(raw.active);
  const savedAt = isoOrFallback(raw.savedAt, EPOCH_ISO);
  const bankTabRaw = typeof raw.bankTab === 'number' ? Math.floor(raw.bankTab) : 0;

  const hpSource = isRecord(raw.hp) ? raw.hp : null;
  const hp =
    hasFixedLayouts && hpSource && typeof hpSource.player === 'number'
      ? {
          player: hpSource.player,
          monster: typeof hpSource.monster === 'number' ? hpSource.monster : null,
        }
      : {
          player: 100,
          monster: active.kind === 'combat' ? getMonster(active.monsterId).hp : null,
        };

  let flags = normalizeFlags(raw.flags);
  const alreadyOwnsStarterLadle =
    Object.values(equipment).includes('polished_ladle') ||
    bank.some((slot) => slot?.itemId === 'polished_ladle') ||
    inventory.some((slot) => slot?.itemId === 'polished_ladle');
  if (alreadyOwnsStarterLadle && !flags.includes(STARTER_LADLE_FLAG)) {
    flags = [...flags, STARTER_LADLE_FLAG];
  }

  return {
    version: SAVE_VERSION,
    savedAt,
    checksum: typeof raw.checksum === 'string' ? raw.checksum : '',
    player: {
      name: 'Herman',
      mannerPoints:
        isRecord(raw.player) && typeof raw.player.mannerPoints === 'number'
          ? raw.player.mannerPoints
          : 0,
    },
    skills: normalizeSkills(raw.skills),
    bank,
    equipment,
    settings: normalizeSettings(raw.settings),
    active,
    quests: normalizeQuests(raw.quests),
    flags,
    combatRound: normalizeCombatRound(raw.combatRound, active),
    inventory,
    bankTab: Math.max(0, Math.min(3, bankTabRaw)),
    lastSimulatedAt: isoOrFallback(raw.lastSimulatedAt, savedAt),
    actionProgressMs:
      typeof raw.actionProgressMs === 'number' &&
      Number.isFinite(raw.actionProgressMs) &&
      raw.actionProgressMs > 0
        ? Math.floor(raw.actionProgressMs)
        : 0,
    hp,
  };
}

/** Rebuild bank/inventory as empty fixed layouts. */
export { emptyBank, emptyInventory, emptyEquipment };
